import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import AppBreadCrumb from '../../common/AppBreadCrumb';
import AppToaster from '../../AppToaster';
import { createFranchisee } from '../../../api/milma_admin/FranchiseeAPI';
import { risapi, getAxiosConfig, handleApiError } from '../../../api/risapi';
import { useNavigate } from 'react-router-dom';

const FranchiseeForm = ({ formType }) => {
    const navigate = useNavigate();
    
    // State Management (Added logo: null)
    const [franchisee, setFranchisee] = useState({
        name: '', region_id: null, pan: '', gstin: '', gst_registration_type: 'regular',
        address: '', state_id: null, district_id: null, pincode: '', logo: null,
    });
    const [user, setUser] = useState({
        name: '', phone_number: '', password: '', password_confirmation: '',
    });
    
    const [regionOptions, setRegionOptions] = useState([]);
    const [stateOptions, setStateOptions] = useState([]);
    const [districtOptions, setDistrictOptions] = useState([]);
    const [loadingDistricts, setLoadingDistricts] = useState(false);

    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const [showToaster, setShowToaster] = useState(false);
    const [toaster, setToaster] = useState({});
    
    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                const [regionRes, stateRes] = await Promise.all([
                    risapi.get('/api/milma-admin/accessible-regions', getAxiosConfig()),
                    risapi.get('/api/states', getAxiosConfig())
                ]);

                if (regionRes.data.regions) setRegionOptions(regionRes.data.regions.map(r => ({ value: r.id, label: r.name })));
                if (stateRes.data.states) setStateOptions(stateRes.data.states.map(s => ({ value: s.id, label: s.name })));
            } catch (error) {
                console.error("Could not load initial master data", error);
            }
        };
        fetchInitialData();
    }, []);

    useEffect(() => {
        if (franchisee.state_id) {
            setLoadingDistricts(true);
            const fetchDistricts = async () => {
                try {
                    const response = await risapi.get(`/api/states/${franchisee.state_id}/districts`, getAxiosConfig());
                    if (response.data.districts) setDistrictOptions(response.data.districts.map(d => ({ value: d.id, label: d.name })));
                } catch (error) {
                    console.error("Could not load districts", error);
                } finally {
                    setLoadingDistricts(false);
                }
            };
            fetchDistricts();
        } else {
            setDistrictOptions([]);
        }
    }, [franchisee.state_id]);

    const handleFranchiseeChange = (e) => {
        const { name, value } = e.target;
        setFranchisee(prev => ({ ...prev, [name]: value }));
        if(errors[`franchisee.${name}`]) setErrors(prev => ({...prev, [`franchisee.${name}`]: null}));
    };
    
    // --- NEW: Logo File Handler ---
    const handleLogoChange = (e) => {
        const file = e.target.files[0];
        setFranchisee(prev => ({ ...prev, logo: file }));
        if(errors['franchisee.logo']) setErrors(prev => ({...prev, ['franchisee.logo']: null}));
    };

    const handleUserChange = (e) => {
        const { name, value } = e.target;
        setUser(prev => ({ ...prev, [name]: value }));
        if(errors[`user.${name}`]) setErrors(prev => ({...prev, [`user.${name}`]: null}));
    };

    const saveForm = async (e) => {
        e.preventDefault();
        setSaving(true);
        setErrors({});
        
        // --- NEW: Map Data to FormData Object to support file uploads ---
        const formData = new FormData();
        
        Object.keys(franchisee).forEach(key => {
            if (franchisee[key] !== null && franchisee[key] !== undefined && franchisee[key] !== '') {
                formData.append(`franchisee[${key}]`, franchisee[key]);
            }
        });
        
        Object.keys(user).forEach(key => {
            if (user[key] !== null && user[key] !== undefined && user[key] !== '') {
                formData.append(`user[${key}]`, user[key]);
            }
        });
        
        createFranchisee(formData, (response) => {
            setSaving(false);
            if(response.franchisee) {
                navigate('/milma-admin/franchisees', { state: { message: 'Franchisee created successfully!' } });
            } else {
                const apiError = response.error || response;
                if (apiError?.response?.status === 422 && apiError?.response?.data?.errors) {
                    setErrors(apiError.response.data.errors);
                } else {
                    handleApiError(apiError, setErrors);
                }
                setToaster({ type: 'danger', heading: 'Error', message: apiError?.response?.data?.message || 'Could not create franchisee.' });
                setShowToaster(true);
            }
        });
    };

    return (
        <>
            <AppBreadCrumb entityName="Franchisee" breadCrumbType="create" />
            <main className="page-content">
                <div className="container-fluid">
                    {showToaster && <AppToaster toaster={toaster} setShowToaster={setShowToaster} />}
                    <div className="card bg-sale">
                        <div className="card-header">
                            <h5>Add New Franchisee</h5>
                        </div>
                        <div className="card-body">
                            <form onSubmit={saveForm}>
                                {/* --- Franchisee Section --- */}
                                <h6 className="mb-3 text-primary">Franchisee Details</h6>
                                <div className="row  p-3 rounded mb-4">
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label">Franchisee Name *</label>
                                        <input className={`form-control ${errors['franchisee.name'] ? 'is-invalid' : ''}`} name="name" value={franchisee.name} onChange={handleFranchiseeChange} />
                                        {errors['franchisee.name'] && <span className="text-danger text-sm">{errors['franchisee.name'][0]}</span>}
                                    </div>

                                    <div className="col-md-6 mb-3">
                                        <label className="form-label">Assigned Region *</label>
                                        <Select 
                                            options={regionOptions}
                                            value={regionOptions.find(opt => opt.value === franchisee.region_id) || null}
                                            onChange={(selected) => {
                                                setFranchisee(prev => ({ ...prev, region_id: selected ? selected.value : null }));
                                                if(errors['franchisee.region_id']) setErrors(prev => ({...prev, ['franchisee.region_id']: null}));
                                            }}
                                            placeholder="Select Region..."
                                            className={errors['franchisee.region_id'] ? 'is-invalid' : ''}
                                        />
                                        {errors['franchisee.region_id'] && <span className="text-danger text-sm">{errors['franchisee.region_id'][0]}</span>}
                                    </div>

                                    {/* --- NEW: Logo File Input --- */}
                                    <div className="col-md-12 mb-3">
                                        <label className="form-label">Franchisee Logo (Optional)</label>
                                        <input 
                                            type="file" 
                                            className={`form-control ${errors['franchisee.logo'] ? 'is-invalid' : ''}`} 
                                            accept="image/*" 
                                            onChange={handleLogoChange} 
                                        />
                                        {errors['franchisee.logo'] && <span className="text-danger text-sm">{errors['franchisee.logo'][0]}</span>}
                                    </div>

                                    <div className="col-md-4 mb-3">
                                        <label className="form-label">PAN Number *</label>
                                        <input className={`form-control ${errors['franchisee.pan'] ? 'is-invalid' : ''}`} name="pan" value={franchisee.pan} onChange={handleFranchiseeChange} style={{ textTransform: 'uppercase' }} />
                                        {errors['franchisee.pan'] && <span className="text-danger text-sm">{errors['franchisee.pan'][0]}</span>}
                                    </div>
                                    <div className="col-md-4 mb-3">
                                        <label className="form-label">GST Registration Type *</label>
                                        <select className="form-control" name="gst_registration_type" value={franchisee.gst_registration_type} onChange={handleFranchiseeChange}>
                                            <option value="regular">Regular</option>
                                            <option value="composition">Composition</option>
                                            <option value="unregistered">Unregistered</option>
                                        </select>
                                    </div>
                                    <div className="col-md-4 mb-3">
                                        <label className="form-label">GSTIN</label>
                                        <input className={`form-control ${errors['franchisee.gstin'] ? 'is-invalid' : ''}`} name="gstin" value={franchisee.gstin} onChange={handleFranchiseeChange} style={{ textTransform: 'uppercase' }} disabled={franchisee.gst_registration_type === 'unregistered'} />
                                        {errors['franchisee.gstin'] && <span className="text-danger text-sm">{errors['franchisee.gstin'][0]}</span>}
                                    </div>
                                    
                                    <div className="col-12 mb-3">
                                        <label className="form-label">Address *</label>
                                        <textarea className={`form-control ${errors['franchisee.address'] ? 'is-invalid' : ''}`} name="address" value={franchisee.address} onChange={handleFranchiseeChange} rows="2"></textarea>
                                        {errors['franchisee.address'] && <span className="text-danger text-sm">{errors['franchisee.address'][0]}</span>}
                                    </div>

                                    <div className="col-md-4 mb-3">
                                        <label className="form-label">State *</label>
                                        <Select 
                                            options={stateOptions}
                                            value={stateOptions.find(opt => opt.value === franchisee.state_id) || null}
                                            onChange={(selected) => {
                                                setFranchisee(prev => ({ 
                                                    ...prev, 
                                                    state_id: selected ? selected.value : null,
                                                    district_id: null
                                                }));
                                                if(errors['franchisee.state_id']) setErrors(prev => ({...prev, ['franchisee.state_id']: null}));
                                            }}
                                            placeholder="Select State..."
                                            className={errors['franchisee.state_id'] ? 'is-invalid' : ''}
                                        />
                                        {errors['franchisee.state_id'] && <span className="text-danger text-sm">{errors['franchisee.state_id'][0]}</span>}
                                    </div>

                                    <div className="col-md-4 mb-3">
                                        <label className="form-label">District/City *</label>
                                        <Select 
                                            options={districtOptions}
                                            value={districtOptions.find(opt => opt.value === franchisee.district_id) || null}
                                            onChange={(selected) => {
                                                setFranchisee(prev => ({ ...prev, district_id: selected ? selected.value : null }));
                                                if(errors['franchisee.district_id']) setErrors(prev => ({...prev, ['franchisee.district_id']: null}));
                                            }}
                                            placeholder={loadingDistricts ? "Loading..." : "Select District..."}
                                            isDisabled={!franchisee.state_id || loadingDistricts}
                                            className={errors['franchisee.district_id'] ? 'is-invalid' : ''}
                                        />
                                        {errors['franchisee.district_id'] && <span className="text-danger text-sm">{errors['franchisee.district_id'][0]}</span>}
                                    </div>

                                    <div className="col-md-4 mb-3">
                                        <label className="form-label">Pincode *</label>
                                        <input className={`form-control ${errors['franchisee.pincode'] ? 'is-invalid' : ''}`} name="pincode" value={franchisee.pincode} onChange={handleFranchiseeChange} />
                                        {errors['franchisee.pincode'] && <span className="text-danger text-sm">{errors['franchisee.pincode'][0]}</span>}
                                    </div>
                                </div>

                                {/* --- User Section --- */}
                                <h6 className="mb-3 text-primary">Admin User Account</h6>
                                <div className="row  p-3 rounded mb-4">
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label">Owner Name *</label>
                                        <input className={`form-control ${errors['user.name'] ? 'is-invalid' : ''}`} name="name" value={user.name} onChange={handleUserChange} />
                                        {errors['user.name'] && <span className="text-danger text-sm">{errors['user.name'][0]}</span>}
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label">Phone Number *</label>
                                        <input className={`form-control ${errors['user.phone_number'] ? 'is-invalid' : ''}`} name="phone_number" value={user.phone_number} onChange={handleUserChange} />
                                        {errors['user.phone_number'] && <span className="text-danger text-sm">{errors['user.phone_number'][0]}</span>}
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label">Password *</label>
                                        <input className={`form-control ${errors['user.password'] ? 'is-invalid' : ''}`} name="password" type="password" value={user.password} onChange={handleUserChange} />
                                        {errors['user.password'] && <span className="text-danger text-sm">{errors['user.password'][0]}</span>}
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label">Confirm Password *</label>
                                        <input className="form-control" name="password_confirmation" type="password" value={user.password_confirmation} onChange={handleUserChange} />
                                    </div>
                                </div>
                                
                                {/* Action Buttons */}
                                <div className="col-12 mt-4">
                                    <div className="text-end">
                                        <button className="btn btn-danger me-2" type="button" onClick={() => navigate('/milma-admin/franchisees')} disabled={saving}>Cancel</button>
                                        <button className="btn btn-primary" type="submit" disabled={saving}>
                                            {saving ? 'Creating...' : 'Create Franchisee & User'}
                                        </button>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </main>
        </>
    );
};

export default FranchiseeForm;